import React, { useMemo } from 'react';
import classNames from 'classnames';
import { TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { TextFieldProps } from '@mui/material/TextField';

// Styles overrides are required
// Dependant on https://github.com/mui-org/material-ui/issues/21409

const useStyles = makeStyles({
  hiddenLabel: {
    '&>div': {
      paddingTop: '0px!important'
    }
  },
  hiddenLabelInput: {
    '&>input.MuiFilledInput-inputHiddenLabel.MuiFilledInput-inputMarginDense': {
      padding: '10px 12px 11px !important'
    }
  }
});

export type AutocompleteInputProps = TextFieldProps & {
  hiddenLabel?: boolean;
  showError?: boolean;
  errorMessage: React.ReactNode | string;
  alwaysShowHelperText: boolean;
};

export const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  hiddenLabel = false,
  variant,
  showError = false,
  errorMessage,
  placeholder,
  helperText,
  alwaysShowHelperText,
  ...textFieldInputProps
}: AutocompleteInputProps) => {
  const classes = useStyles();

  const helperTextMessage = useMemo(
    () => (showError && errorMessage) || (alwaysShowHelperText && helperText),
    [showError, errorMessage, alwaysShowHelperText, helperText]
  );
  const { InputProps, className, inputProps, ...rest } = textFieldInputProps;

  const autocompleteInputProps = useMemo(() => {
    const autocompleteInputProps = {
      ...InputProps,
      className: classNames(InputProps?.className, {
        [classes.hiddenLabelInput]: hiddenLabel
      }),
      inputProps
    };
    return autocompleteInputProps;
  }, [InputProps, classes.hiddenLabelInput, hiddenLabel, inputProps]);

  if (hiddenLabel) {
    return (
      <TextField
        error={showError}
        helperText={helperTextMessage}
        className={classNames(className, {
          [classes.hiddenLabel]: hiddenLabel
        })}
        placeholder={placeholder}
        variant={variant}
        InputProps={autocompleteInputProps}
        hiddenLabel={hiddenLabel}
        {...rest}
      />
    );
  }

  return (
    <TextField
      error={showError}
      helperText={helperTextMessage}
      variant={variant}
      {...textFieldInputProps}
      placeholder={placeholder}
    />
  );
};

export default AutocompleteInput;
